import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS } from '../ruleExpectations.js'
import {
  buildEligibleStudentApply,
  buildEvaluationRecord,
  closeEvaluationDraft,
  emptyEvalDates,
  markChangeRequestApplied,
  buildChangeRequest
} from '@/utils/studentEvaluation.js'

// DESTRUCTIVE: the two outcome endings that write the student.
//
// 1. Eligible on a live 504 — the write chairs could not do before (plan is
//    504). Asserts plan → IEP, CM set, planStatus cleared, eval closed.
// 2. Not eligible on a fillable shell — close the eval and delete the student
//    (create-roles). Asserts the student is gone.
//
// Not-eligible / keep-504 stays in evalCardLifecycle.

const OTHER_CM = 'smoke-other-cm'
const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 75000

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function pollStudent(ref, check) {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let last = 'no read yet'
  while (Date.now() < deadline) {
    let state
    try {
      const snap = await getDoc(ref)
      state = { denied: false, exists: snap.exists(), data: snap.exists() ? snap.data() : null }
    } catch (err) {
      if (err?.code !== 'permission-denied') throw err
      state = { denied: true, exists: null, data: null }
    }
    const result = check(state)
    if (result) return result
    last = state.denied
      ? 'read denied'
      : `exists=${state.exists} evalCaseManagerId=${JSON.stringify(state.data?.app?.evalCaseManagerId ?? null)}`
    await sleep(POLL_INTERVAL_MS)
  }
  throw new Error(
    `Timed out after ${POLL_TIMEOUT_MS / 1000}s waiting for Cloud Function (last: ${last}). ` +
      'Are functions deployed to this project?'
  )
}

export default {
  id: 'evalOutcomeApply',
  label: 'Eval outcome apply (eligible + shell remove)',
  description:
    'Eligible writes plan + CM and closes the eval on a live 504. Not eligible on a shell deletes the student. Needs deployed functions + the 2026-08-30 outcome rules.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const uid = ctx.user.uid

    if (!ctx.schoolId) {
      step.block('setup', 'Select a school before running the eval outcome smoke.')
      return
    }
    if (
      !RULE_EXPECTATIONS.studentCreate(role) ||
      !RULE_EXPECTATIONS.studentEvaluationCreate(role)
    ) {
      step.block(
        'setup',
        `Role ${role} cannot create students/evaluations — use a sped chair, psych, or admin.`
      )
      return
    }

    const liveRef = doc(collection(db, 'students'))
    const liveEvalRef = doc(collection(db, 'studentEvaluations'))
    const shellRef = doc(collection(db, 'students'))
    const shellEvalRef = doc(collection(db, 'studentEvaluations'))

    try {
      await step('create 504 student (plan CM is someone else)', async () => {
        await setDoc(liveRef, {
          app: {
            studentData: {
              firstName: 'SMOKE',
              lastName: `EvalElig ${ctx.runId.slice(-8)}`,
              plan: '504',
              grade: '8',
              ssid: `SMOKE-EVALELIG-${ctx.runId}`,
              caseManagerId: OTHER_CM
            },
            accommodations: { instruction: '', assessment: '' },
            schedule: { classServices: [] },
            staffIds: []
          },
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          schoolId: ctx.schoolId
        })
        return `students/${liveRef.id}`
      })

      await step('create IEP eval assigned to you', async () => {
        const record = buildEvaluationRecord({
          studentId: liveRef.id,
          schoolId: ctx.schoolId,
          evalType: 'IEP',
          caseManagerId: uid,
          createdBy: uid,
          dates: { ...emptyEvalDates(), referral: '2026-08-01', consent: '2026-08-10' },
          source: 'manual'
        })
        await setDoc(liveEvalRef, {
          ...record,
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return `studentEvaluations/${liveEvalRef.id}`
      })

      await step('CF mirrors eval CM onto the 504 student', async () => {
        return pollStudent(liveRef, ({ denied, data }) => {
          if (denied || !data) return null
          if (data.app?.evalCaseManagerId === uid) return 'mirror set'
          return null
        })
      })

      await step('eligible apply writes IEP + CM and clears planStatus', async () => {
        const built = buildEligibleStudentApply({ plan: 'IEP', caseManagerId: uid })
        if (!built.ok)
          throw new Error(built.errors[0]?.message || 'buildEligibleStudentApply failed')
        await updateDoc(liveRef, {
          'app.studentData.plan': built.patch.plan,
          'app.studentData.caseManagerId': built.patch.caseManagerId,
          'app.studentData.planStatus': deleteField()
        })
        const snap = await getDoc(liveRef)
        const sd = snap.data()?.app?.studentData || {}
        if (sd.plan !== 'IEP') throw new Error(`plan is ${sd.plan}, expected IEP`)
        if (sd.caseManagerId !== uid) throw new Error('caseManagerId was not set to the new CM')
        if (sd.planStatus === 'initial') throw new Error('planStatus is still initial')
        return 'plan IEP, CM set, planStatus cleared'
      })

      await step('close eval as eligible (archived)', async () => {
        const current = (await getDoc(liveEvalRef)).data()
        const now = new Date().toISOString()
        const result = closeEvaluationDraft(current, {
          outcome: 'eligible',
          closedDate: '2026-08-30',
          changeRequest: markChangeRequestApplied(
            buildChangeRequest({
              requestedBy: uid,
              requestedAt: now,
              proposed: { plan: 'IEP', caseManagerId: uid, retire504: false }
            }),
            { appliedBy: uid, appliedAt: now }
          ),
          archive: true,
          archivedBy: uid
        })
        if (!result.ok) throw new Error(result.errors[0]?.message || 'closeEvaluationDraft failed')
        await updateDoc(liveEvalRef, { ...result.patch, updatedAt: serverTimestamp() })
        return 'closed eligible + archived'
      })

      await step('create fillable shell + eval', async () => {
        await setDoc(shellRef, {
          app: {
            studentData: {
              firstName: 'SMOKE',
              lastName: `EvalShellRm ${ctx.runId.slice(-8)}`,
              plan: 'IEP',
              planStatus: 'initial',
              grade: '7',
              ssid: `SMOKE-EVALSHELLM-${ctx.runId}`,
              caseManagerId: ''
            },
            accommodations: { instruction: '', assessment: '' },
            schedule: { classServices: [] },
            staffIds: []
          },
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          schoolId: ctx.schoolId
        })
        const record = buildEvaluationRecord({
          studentId: shellRef.id,
          schoolId: ctx.schoolId,
          evalType: 'IEP',
          caseManagerId: uid,
          createdBy: uid,
          dates: { ...emptyEvalDates(), referral: '2026-08-01', consent: '2026-08-10' },
          source: 'manual'
        })
        await setDoc(shellEvalRef, {
          ...record,
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return `students/${shellRef.id}`
      })

      await step('not eligible: close the eval (archived)', async () => {
        const current = (await getDoc(shellEvalRef)).data()
        const result = closeEvaluationDraft(current, {
          outcome: 'notEligible',
          closedDate: '2026-08-30',
          archive: true,
          archivedBy: uid
        })
        if (!result.ok) throw new Error(result.errors[0]?.message || 'closeEvaluationDraft failed')
        await updateDoc(shellEvalRef, { ...result.patch, updatedAt: serverTimestamp() })
        return 'closed notEligible + archived'
      })

      await step('not eligible: remove the Initial shell', async () => {
        // deleteDoc resolving is the proof. A follow-up get of the missing
        // student is denied: the students get rule reads resource.data, and a
        // document that is already gone has no resource. That denial is not
        // a failed delete.
        await deleteDoc(shellRef)
        return 'student deleted'
      })
    } finally {
      await deleteDoc(liveEvalRef).catch(() => {})
      await deleteDoc(liveRef).catch(() => {})
      await deleteDoc(shellEvalRef).catch(() => {})
      await deleteDoc(shellRef).catch(() => {})
    }
  }
}
